import { ChangeDetectionStrategy, Component, computed, inject, input, Signal, signal } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';

import { InterventionsByStatus } from '@connect-services/client/components/field-intervention-list/field-intervention-list.component';
import { MapComponent } from '@connect-services/client/components/map/map.component';
import { FieldInterventionEnum } from '@connect-services/client/models/panel.model';
import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { ContentLayoutService } from '@connect-services/client/services/content-layout.service';
import { FieldInterventionService } from '@connect-services/client/services/field-intervention.service';
import { SelectionService } from '@connect-services/client/services/map/selection/selection.service';
import { MapFieldInterventionService } from '@connect-services/client/services/map-field-intervention.service';
import { MapModal, MapModalService } from '@connect-services/client/services/map-modal.service';
import { MapSourceService } from '@connect-services/client/services/map-source.service';
import { PanelService } from '@connect-services/client/services/panel.service';
import { SelectZoneLayerService } from '@connect-services/client/services/select-zone-layer.service';
import { SourcesService } from '@connect-services/client/services/sources.service';
import { FieldIntervention } from '@connect-services/src/sdk/api';
import { RessourcesModule } from '@ressources/client/ressources.module';
import StatusEnum = FieldIntervention.StatusEnum;

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [RessourcesModule],
    selector: 'app-field-intervention-list-item',
    styleUrls: ['./field-intervention-list-item.component.scss'],
    templateUrl: './field-intervention-list-item.component.html',
})
export class FieldInterventionListItemComponent {
    public readonly interventionsByStatus = input.required<InterventionsByStatus>();
    public readonly isOpeningFieldIntervention = signal<boolean>(false);
    public readonly translatedStatusKey: Signal<string> = computed(() => {
        if (this.interventionsByStatus().name === StatusEnum.DONE) {
            return 'doneFemale';
        }

        return this.interventionsByStatus().name;
    });

    protected readonly statusesIconsProperties = FieldInterventionService.statusesIconsProperties;

    private readonly activateRoute = inject(ActivatedRoute);
    private readonly contentLayoutService = inject(ContentLayoutService);
    private readonly mapFieldInterventionService = inject(MapFieldInterventionService);
    private readonly mapModalService = inject(MapModalService);
    private readonly mapSourceService = inject(MapSourceService);
    private readonly panelService = inject(PanelService);
    private readonly router = inject(Router);
    private readonly selectionService = inject(SelectionService);
    private readonly selectZoneLayerService = inject(SelectZoneLayerService);
    private readonly sourcesService = inject(SourcesService);

    public async openFieldInterventionDetail(intervention: FieldIntervention): Promise<void> {
        if (this.isOpeningFieldIntervention()) {
            return;
        }

        this.isOpeningFieldIntervention.set(true);
        const fieldInterventionType = this.getFieldInterventionType(intervention);
        this.setFieldInterventionLayout(fieldInterventionType);

        try {
            await this.cleanMapBeforeOpeningFieldIntervention();

            this.panelService.setPanel(null);

            await this.navigateToFieldIntervention(intervention, fieldInterventionType);
        } finally {
            this.isOpeningFieldIntervention.set(false);
        }
    }

    private async cleanMapBeforeOpeningFieldIntervention(): Promise<void> {
        this.mapModalService.close(MapModal.SELECTION);
        this.mapModalService.close(MapModal.FILTER);
        this.selectionService.resetSelectionState();

        this.mapFieldInterventionService.cancelPendingDraws();

        await this.selectZoneLayerService.removeLayers();

        this.sourcesService.sources.set([]);
        this.mapSourceService.clearSources(MapComponent.backgroundSource(), []);
    }

    private getFieldInterventionType(intervention: FieldIntervention): FieldInterventionEnum {
        if (intervention.status === FieldIntervention.StatusEnum.TO_PLAN) {
            return FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_PLANNING;
        }

        return FieldInterventionEnum.FIELD_INTERVENTION_DASHBOARD;
    }

    private navigateToFieldIntervention(
        intervention: FieldIntervention,
        fieldInterventionType: FieldInterventionEnum,
    ): Promise<boolean> {
        const routePath =
            fieldInterventionType === FieldInterventionEnum.FIELD_INTERVENTION_ENTITY_PLANNING
                ? RoutePathEnum.FIELD_INTERVENTION_PLANNING
                : RoutePathEnum.FIELD_INTERVENTION_DASHBOARD;

        return this.router.navigate([`${intervention.id}/${routePath}`], {
            relativeTo: this.activateRoute,
        });
    }

    private setFieldInterventionLayout(fieldInterventionType: FieldInterventionEnum): void {
        const gridParams = this.contentLayoutService.gridParamsByFieldInterventionType[fieldInterventionType];

        this.contentLayoutService.gridParams.set(gridParams);

        const THIRDS_LAYOUT_KEY = 2;
        const defaultGridLayout = Object.values(gridParams)[THIRDS_LAYOUT_KEY];

        this.contentLayoutService.mapSizeType.set(defaultGridLayout);
        this.contentLayoutService.initialMapSizeType.set(defaultGridLayout);
    }
}
